import * as React from "react";

import { IsValidCheckmark } from "../Components/IsValidCheckmark";
import { ExcludeCharacterFilterTypes, filterCharacters } from "./Common/ExcludeCharacterFilters";
import { MaxLengthMessage } from "./Common/MaxLenghtMessage";
import { IInputFieldProps } from "./IInputFieldProps";
import "./inputfield.scss";
import { wrapInputInGroup } from "./Utils/wrapInputInGroup";

export interface InputFieldProps extends IInputFieldProps<HTMLInputElement> {
  icon?: "search" | string;
  value?: string;
  children?: React.ReactNode;
  maxLength?: number;
  showMaxLengthMessage?: boolean;
  maxLengthReachedMessage?: string;
  type?: string;
  disabled?: boolean;
  ariaLabelledby?: string;
  ariaDescribedBy?: string;
  inputRef?: React.RefCallback<HTMLInputElement>;
  isInvalid?: boolean;
  isDirty?: boolean;
  isTouched?: boolean;
  showIsValidCheckmark?: boolean;
}

export const InputField = (props: InputFieldProps) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const genericIcon = props.icon && props.icon !== "search" ? <span className={props.icon}></span> : "";
  const icon = <>{props.icon === "search" ? <span className={`${props.icon}-icon`}> </span> : genericIcon}</>;
  const warningMessage = props.showMaxLengthMessage && props.maxLength && (
    <MaxLengthMessage
      id={props.id}
      value={props.value}
      maxLength={props.maxLength}
      maxLengthReachedMessage={props.maxLengthReachedMessage}
    />
  );

  const onKeyPress = (event) => {
    if (props.excludeCharactersFilter) {
      const newKey = filterCharacters(
        event.key,
        props.excludeCharactersFilter || ExcludeCharacterFilterTypes.Default
      );
      if (newKey === "") {
        event.preventDefault();
        return false;
      }
    }
  };

  const onPaste = (event) => {
    if (props.excludeCharactersFilter) {
      event.preventDefault();
      if (!inputRef.current) {
        return true;
      }
      const pastedData = event.clipboardData.getData("Text");
      const cursorStart = (inputRef.current as unknown as HTMLInputElement).selectionStart || 0;
      const cursorEnd = (inputRef.current as unknown as HTMLInputElement).selectionEnd || 0;
      if (props.onChange) {
        const filteredPastedData = filterCharacters(pastedData, props.excludeCharactersFilter);
        const newValue = `${(props.value || "").slice(0, cursorStart)}${filteredPastedData}${(
          props.value || ""
        ).slice(cursorEnd)}`;

        props.onChange({
          target: {
            value: newValue
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);

        window.requestAnimationFrame(() => {
          if (!inputRef.current) {
            return;
          }
          (inputRef.current as unknown as HTMLInputElement).selectionStart =
            cursorStart + filteredPastedData.length;
          (inputRef.current as unknown as HTMLInputElement).selectionEnd =
            cursorStart + filteredPastedData.length;
        });
      }
      return false;
    }
  };

  let input = (
    <>
      {icon}
      <input
        {...props.input}
        type={props.type}
        ref={(ref) => {
          inputRef.current = ref;
          props.inputRef && props.inputRef(ref);
        }}
        className={`form-control form-control--text ${props.className ?? ""}`}
        style={{ display: props.displayStyle }}
        {...(props.id ? { id: props.id } : {})}
        {...(props.name ? { name: props.name } : {})}
        placeholder={props.placeholder}
        value={props.value || ""}
        onPaste={onPaste}
        onKeyPress={onKeyPress}
        onChange={props.onChange}
        onBlur={props.onBlur}
        maxLength={props.maxLength}
        disabled={props.disabled}
        aria-labelledby={props.ariaLabelledby}
        aria-describedby={props.ariaDescribedBy}
        autoComplete={props.autoComplete}
      />
    </>
  );

  if (props.showIsValidCheckmark) {
    input = wrapInputInGroup(
      <>
        {input}
        <IsValidCheckmark isInvalid={props.isInvalid ?? false} hasValue={(props.value ?? "") != ""} />
      </>
    );
  } else if (props.icon && props.icon !== "search") {
    input = wrapInputInGroup(input);
  }
  return (
    <>
      {input}
      {warningMessage}
    </>
  );
};
